import React, { useContext } from 'react'
import { AuditPromiseClient } from '@trustero/trustero-api-web/lib/audit/audit_grpc_web_pb'
import {
  AuditRecord,
  GetReportRequest,
  ReportType,
} from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import log from 'loglevel'
import { toast } from 'react-toastify'
import { BasicDropdownMenuItem } from 'src/components/Reusable/BasicDropdown/BasicDropdown.styles'
import { useAuthorizedGrpcClient } from '../../../../adapter'
import { useAudit } from '../../../../components/async/model/audit'
import { Spinner, ThrobberContext } from '../../../../Throbber'
import { mimicAnchorDownload } from '../../../GRCDashboards/Compliance/Download/hooks'

export const DownloadWorkPapers = ({
  eventKey,
  auditId,
}: {
  eventKey: string
  auditId: string
}): JSX.Element => {
  const downloadWorkpapers = useDownloadWorkpapersByAudit()
  const { setThrobberState } = useContext(ThrobberContext)
  const { data: audit, isLoading } = useAudit(auditId)

  if (!audit || isLoading) {
    return <></>
  }

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault()
    setThrobberState({
      isShown: true,
      Display: <Spinner color="primary" size="xl" />,
    })
    try {
      await downloadWorkpapers(audit)
    } finally {
      setThrobberState({
        isShown: false,
        Display: <></>,
      })
    }
  }

  return (
    <BasicDropdownMenuItem eventKey={eventKey} onClick={handleDownload}>
      Work papers (.zip)
    </BasicDropdownMenuItem>
  )
}

/**
 * @deprecated - This is an older implementation not leveraging content store
 *
 * Please try to use below hook instead which references a content store hash
 */
export const useDownloadWorkpapersByAudit = (): ((
  audit?: AuditRecord,
  auditId?: string,
) => void) => {
  const client = useAuthorizedGrpcClient(AuditPromiseClient)

  return async (audit?: AuditRecord, auditId?: string) => {
    const id = auditId || audit?.getId() || ''
    try {
      const request = new GetReportRequest()
        .setAuditId(new StringValue().setValue(id))
        .setType(ReportType.WORK_PAPERS)
      const wp = await client.getReport(request)
      const uri = wp.getUri()
      // Notify user if we have generated the workpapers and they will receive an email when ready
      uri
        ? mimicAnchorDownload(wp.getUri())
        : toast.info(
            'Work Papers are being generated. You will be notified by email when they are ready.',
          )
    } catch (e) {
      log.error(`Error generating WorkPapers for ${id}`, e)
      toast.error('There was a problem generating the Work Papers.')
    }
  }
}

// This is the newer version of the hook to grab workpapers from trustero content store
// This is only used by the workpapers download modal
export const useGetWorkpapersUrl = (): ((contentId: string) => void) => {
  const client = useAuthorizedGrpcClient(AuditPromiseClient)

  return async (contentId: string, auditId?: string) => {
    const request = new GetReportRequest()
      .setType(ReportType.WORK_PAPERS)
      .setContentId(new StringValue().setValue(contentId))
    if (auditId !== undefined) {
      request.setAuditId(new StringValue().setValue(auditId))
    }
    try {
      const wp = await client.getReport(request)
      const uri = wp.getUri()
      if (uri) mimicAnchorDownload(wp.getUri())
    } catch (e) {
      log.error(`Error generating WorkPapers for contentId: ${contentId}`, e)
      toast.error('There was a problem generating the Work Papers.')
    }
  }
}
